import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';
import api from '../services/api';

interface ActivationUserItem {
  user_id: number;
  profile_id?: string;
  name: string;
  email: string;
  status: string;
  activation_started_at?: string | null;
  activation_expires_at?: string | null;
  days_remaining: number;
  reactivation_requested_at?: string | null;
  lead_cost_inr: number;
}

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
};

const statusChipColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'ACTIVE') return 'success';
  if (status === 'REACTIVATION_REQUESTED') return 'warning';
  if (status === 'EXPIRED') return 'error';
  return 'default';
};

const AdminActivationStatusPage: React.FC = () => {
  const [items, setItems] = useState<ActivationUserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/admin/activation-status', {
        params: {
          search: searchText.trim() || undefined,
          status_filter: statusFilter,
        },
      });
      setItems(response.data.items || []);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load activation status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [statusFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      void loadData();
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [searchText]);

  const handleApprove = async (userId: number) => {
    try {
      await api.post(`/users/admin/users/${userId}/activation/approve`, {});
      toast.success('Profile activated for 1 year');
      await loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to approve activation');
    }
  };

  const handleSetLeadCost = async (user: ActivationUserItem) => {
    const input = window.prompt(`Set lead cost for ${user.name} (Points per lead):`, String(user.lead_cost_inr || 10));
    if (!input) return;
    const value = Number(input);
    if (!Number.isFinite(value) || value < 1) {
      toast.error('Enter a valid amount');
      return;
    }

    try {
      await api.put(`/users/admin/users/${user.user_id}/lead-cost`, { lead_cost_inr: Math.floor(value) });
      toast.success('Lead cost updated');
      await loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update lead cost');
    }
  };

  const handleResetLeadCost = async (userId: number) => {
    try {
      await api.post(`/users/admin/users/${userId}/lead-cost/reset`, {});
      toast.success('Lead cost reset to default 10 Points');
      await loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reset lead cost');
    }
  };

  return (
    <AdminLayout currentPage="activation-status">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Activation Status
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage 1-year profile activation windows and per-user lead pricing.
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr 140px' }, gap: 1.5 }}>
            <TextField
              label="Search by Profile ID, Name, Email"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <TextField select label="Status" size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="ALL">All Statuses</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
              <MenuItem value="PENDING_APPROVAL">Pending Approval</MenuItem>
              <MenuItem value="REACTIVATION_REQUESTED">Reactivation Requested</MenuItem>
            </TextField>
            <Button variant="outlined" onClick={() => setSearchText('')}>Clear</Button>
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Alert severity="info">No users found for the current activation filter.</Alert>
      ) : (
        <Card>
          <CardContent>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 850 }}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Profile</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Activation Window</strong></TableCell>
                    <TableCell align="right"><strong>Lead Cost</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.user_id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.profile_id || '-'}</Typography>
                        <Typography variant="body2">{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={item.status.replace(/_/g, ' ')} color={statusChipColor(item.status)} />
                        {item.status === 'REACTIVATION_REQUESTED' && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }} color="text.secondary">
                            Requested: {formatDate(item.reactivation_requested_at)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">From</Typography>
                        <Typography variant="body2">{formatDate(item.activation_started_at)}</Typography>
                        <Typography variant="caption" color="text.secondary">To</Typography>
                        <Typography variant="body2">{formatDate(item.activation_expires_at)}</Typography>
                        <Typography variant="caption" color="text.secondary">Days left: {item.days_remaining}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{item.lead_cost_inr} Points</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" variant="outlined" onClick={() => handleSetLeadCost(item)}>
                            Set Lead Cost
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => handleResetLeadCost(item.user_id)}>
                            Reset
                          </Button>
                          {(item.status === 'EXPIRED' || item.status === 'REACTIVATION_REQUESTED' || item.status === 'PENDING_APPROVAL') && (
                            <Button size="small" variant="contained" onClick={() => handleApprove(item.user_id)}>
                              Activate
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
};

export default AdminActivationStatusPage;
